import type { PortalAppointment } from '../../core/models/portal-appointment';

const FINISHED_STATUSES: ReadonlySet<string> = new Set(['Completed', 'Cancelled']);

/**
 * A case's appointments as the customer reads them: what is still ahead, soonest first, then what
 * is finished or called off, in the order the server sent it (newest first).
 *
 * **Ahead is decided by status, not by the clock.** Comparing with "now" would need the handset's
 * zone database, which this client does not trust; the office is what moves a meeting to completed.
 * `YYYY-MM-DD HH:mm` strings sort correctly as text, and a meeting with no agreed time goes last.
 */
export function groupCaseAppointments(appointments: readonly PortalAppointment[]): {
    upcoming: PortalAppointment[];
    earlier: PortalAppointment[];
} {
    const upcoming = appointments
        .filter((appointment) => !FINISHED_STATUSES.has(appointment.status))
        .sort((left, right) => compare(sortKey(left), sortKey(right)));

    const earlier = appointments.filter((appointment) => FINISHED_STATUSES.has(appointment.status));

    return { upcoming, earlier };
}

// `~` sorts after every digit. A date without a time counts as not agreed, as the card shows it.
function sortKey(appointment: PortalAppointment): string {
    return appointment.scheduledDate === null || appointment.scheduledTime === null
        ? '~'
        : `${appointment.scheduledDate} ${appointment.scheduledTime}`;
}

// Code-unit order rather than `localeCompare`, which is free to place `~` before digits.
function compare(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0;
}
